import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, CheckCircle2, Shield } from 'lucide-react';

export default function SmartContractAudit() {
  const [currentChallenge, setCurrentChallenge] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedVulnerabilities, setSelectedVulnerabilities] = useState(new Set());
  const [completed, setCompleted] = useState(false);
  const [feedback, setFeedback] = useState('');

  const challenges = [
    {
      title: "Reentrancy Vulnerability",
      code: `pragma solidity ^0.8.0;
contract Bank {
    mapping(address => uint) balance;
    
    function withdraw(uint amount) public {
        require(balance[msg.sender] >= amount);
        (bool success, ) = msg.sender.call{value: amount}("");
        require(success);
        balance[msg.sender] -= amount;
    }
}`,
      vulnerabilities: [
        { id: 1, text: "Reentrancy: State changed AFTER external call", severity: "CRITICAL" },
        { id: 2, text: "No check for call success before state update", severity: "HIGH" },
      ],
      correctIds: [1, 2],
      explanation: "The contract sends funds before updating balance, allowing reentrancy attacks. Always use checks-effects-interactions pattern."
    },
    {
      title: "Integer Overflow",
      code: `pragma solidity ^0.7.0;
contract Wallet {
    uint8 public balance = 255;
    
    function deposit(uint8 amount) public {
        balance += amount;
    }
}`,
      vulnerabilities: [
        { id: 1, text: "uint8 max is 255, adding will overflow", severity: "CRITICAL" },
        { id: 2, text: "No SafeMath library used", severity: "HIGH" },
      ],
      correctIds: [1, 2],
      explanation: "Solidity 0.8.0+ has automatic overflow checks, but older versions need SafeMath. Always validate input ranges."
    },
    {
      title: "Front-Running Risk",
      code: `pragma solidity ^0.8.0;
contract Auction {
    uint public highestBid;
    
    function bid(uint amount) public {
        require(amount > highestBid);
        highestBid = amount;
    }
}`,
      vulnerabilities: [
        { id: 1, text: "Transaction order visible in mempool (front-running risk)", severity: "HIGH" },
        { id: 2, text: "No access control on bidding", severity: "MEDIUM" },
      ],
      correctIds: [1, 2],
      explanation: "Miners can see transactions before they're confirmed and insert their own. Use commit-reveal schemes or private mempools."
    }
  ];

  const challenge = challenges[currentChallenge];

  const toggleVulnerability = (id) => {
    const newSet = new Set(selectedVulnerabilities);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedVulnerabilities(newSet);
  };

  const checkAnswer = () => {
    const correctSet = new Set(challenge.correctIds);
    const isCorrect = 
      selectedVulnerabilities.size === correctSet.size &&
      [...selectedVulnerabilities].every(id => correctSet.has(id));

    if (isCorrect) {
      setScore(score + 1);
      setFeedback(`✓ Correct! ${challenge.explanation}`);
      setTimeout(() => {
        if (currentChallenge < challenges.length - 1) {
          setCurrentChallenge(currentChallenge + 1);
          setSelectedVulnerabilities(new Set());
          setFeedback('');
        } else {
          setCompleted(true);
        }
      }, 3000);
    } else {
      setFeedback(`✗ Incorrect. ${challenge.explanation}`);
    }
  };

  if (completed) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-4">
        <Card className="w-full max-w-md p-8 text-center">
          <div className="text-5xl mb-4">🔒</div>
          <h2 className="text-2xl font-bold mb-4">Audit Complete!</h2>
          <p className="text-4xl font-bold text-green-400 mb-4">{score}/{challenges.length}</p>
          <p className="text-gray-300 mb-6">You've audited all contracts!</p>
          <Button onClick={() => window.location.href = '/games'} className="w-full">
            Back to Games
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">🔍 Smart Contract Auditor</h1>
          <div className="flex justify-between items-center">
            <p className="text-gray-300">Challenge {currentChallenge + 1}/{challenges.length}</p>
            <Badge className="bg-green-500">{score} Correct</Badge>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Code Section */}
          <Card className="p-6 bg-slate-800 border-slate-700">
            <h3 className="text-xl font-bold mb-4 text-white">{challenge.title}</h3>
            <pre className="bg-slate-900 p-4 rounded overflow-x-auto text-sm text-green-400 mb-4">
              {challenge.code}
            </pre>
          </Card>

          {/* Vulnerabilities Section */}
          <div>
            <h3 className="text-xl font-bold mb-4 text-white">🐛 Select Vulnerabilities</h3>
            <div className="space-y-3 mb-6">
              {challenge.vulnerabilities.map((vuln) => (
                <div
                  key={vuln.id}
                  onClick={() => toggleVulnerability(vuln.id)}
                  className={`p-4 rounded cursor-pointer transition ${
                    selectedVulnerabilities.has(vuln.id)
                      ? 'bg-red-500 border-2 border-red-600'
                      : 'bg-slate-700 border-2 border-slate-600 hover:border-slate-500'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-1 ${selectedVulnerabilities.has(vuln.id) ? 'text-white' : 'text-gray-400'}`}>
                      {selectedVulnerabilities.has(vuln.id) ? '✓' : '○'}
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-semibold">{vuln.text}</p>
                      <Badge className={vuln.severity === 'CRITICAL' ? 'bg-red-600' : 'bg-yellow-600'}>
                        {vuln.severity}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {feedback && (
              <div className={`p-4 rounded mb-6 ${feedback.startsWith('✓') ? 'bg-green-900' : 'bg-red-900'}`}>
                <p className={feedback.startsWith('✓') ? 'text-green-200' : 'text-red-200'}>
                  {feedback}
                </p>
              </div>
            )}

            <Button onClick={checkAnswer} className="w-full" size="lg">
              Check Answer
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
